import { useEffect, useState, type ReactNode } from 'react';
import { Pill, Table, type Row, type Tone } from '../ui/Block';
import { fmtDate, fmtDur, get } from '../lib/api';
import { useAdmin } from '../ui/Shell';
import { DBlock } from './dataShared';

export type Outcome = 'worked' | 'fixed' | 'failed' | 'resolved' | 'stopped' | 'waiting' | 'running' | 'live' | 'no_audio' | 'none';
export const OUTCOME: Record<Outcome, { label: string; tone: Tone; says: string }> = {
  failed: { label: 'FAILED', tone: 'bad', says: 'Failed, not fixed' },
  fixed: { label: 'FIXED', tone: 'ok', says: 'Failed, then worked on a retry' },
  worked: { label: 'WORKED', tone: 'ok', says: 'Worked' },
  running: { label: 'RUNNING', tone: 'info', says: 'Running now' },
  waiting: { label: 'WAITING', tone: 'mute', says: 'Waiting to start' },
  live: { label: 'LIVE', tone: 'live', says: 'Recording live now' },
  no_audio: { label: 'NO AUDIO', tone: 'bad', says: 'Ended without any audio' },
  resolved: { label: 'SET ASIDE', tone: 'mute', says: 'Failed; staff set it aside' },
  stopped: { label: 'STOPPED', tone: 'mute', says: 'Stopped before it finished' },
  none: { label: 'NOTHING YET', tone: 'mute', says: 'Nothing recorded yet' },
};
export const OUTCOME_OPTIONS = [{ value: '', label: 'Any outcome' }, ...(['failed', 'no_audio', 'fixed', 'worked', 'running', 'waiting', 'live', 'resolved', 'stopped'] as Outcome[]).map((o) => ({ value: o, label: OUTCOME[o].says }))];
export const outcomePill = (o?: string | null) => ({ label: OUTCOME[o as Outcome]?.label ?? (o ?? '—').toUpperCase(), tone: OUTCOME[o as Outcome]?.tone ?? ('mute' as Tone) });

export interface Retries { customer: number; staff: number }
export function retriesText(r?: Retries | null): string | undefined {
  if (!r || (!r.customer && !r.staff)) return undefined;
  if (r.customer + r.staff === 1) return `retried once by ${r.customer ? 'the customer' : 'staff'}`;
  if (!r.staff) return `${r.customer} retries by the customer`;
  if (!r.customer) return `${r.staff} retries by staff`;
  return `${r.customer + r.staff} retries: ${r.customer} by the customer, ${r.staff} by staff`;
}

export const shortUrl = (u: string, max = 48) => { const s = u.replace(/^https?:\/\/(www\.)?/, ''); return s.length > max ? `${s.slice(0, max - 1)}…` : s; };

export function sourceOf(t: { live?: boolean; jobSource?: string | null; sourceKind?: string | null; sourceUrl?: string | null; site?: string | null; fileName?: string | null; captureSourceClass?: string | null }): { tag: string; text: ReactNode } {
  const url = t.sourceUrl;
  if (t.live) return { tag: t.captureSourceClass === 'youtube' ? 'YouTube live' : t.captureSourceClass === 'radio' ? 'Stream' : 'Live mic', text: url ? <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mono">{shortUrl(url)}</a> : 'microphone' };
  if (url) return { tag: t.site ?? 'Link', text: <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mono" title={url}>{shortUrl(url)}</a> };
  return { tag: 'File', text: t.fileName ?? 'uploaded file' };
}

export function SourceLine({ s }: { s: { tag: string; text: ReactNode } }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, maxWidth: '100%' }}>
      <span className="pill" style={{ padding: '1px 6px', borderRadius: 4, background: 'var(--fill-08)', color: 'var(--ink-80)', fontSize: 10.5, flex: 'none' }}>{s.tag}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{s.text}</span>
    </span>
  );
}

export function CustomerLink({ email, onPick }: { email?: string | null; onPick: (email: string) => void }) {
  if (!email) return <span style={{ color: 'var(--ink-40)' }}>—</span>;
  return (
    <button type="button" title={`Only ${email}'s transcriptions`} onClick={(e) => { e.stopPropagation(); onPick(email); }}
      style={{ all: 'unset', cursor: 'pointer', display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5, color: 'var(--brand)' }}>{email}</button>
  );
}

export interface Who { kind: 'customer' | 'staff'; label: string }
export interface StoryEvent {
  at: string; kind: string; who?: Who; reason?: string | null; bulk?: boolean;
  n?: number; waitedS?: number | null; tookS?: number | null; outcome?: string; error?: string | null; errorShown?: string | null; backfilled?: boolean;
  audioS?: number | null; lines?: number; neverStarted?: boolean; endReason?: string | null; bytesIn?: number | null;
}
export interface Attempt { n: number; queuedAt?: string | null; startedAt?: string | null; endedAt?: string | null; waitedS?: number | null; tookS?: number | null; outcome: string; error?: string | null; errorShown?: string | null; errorCode?: string | null; worker?: string | null; backfilled: boolean }
export interface Story { events: StoryEvent[]; attempts: Attempt[]; complete: boolean }

const TRY: Record<string, { says: string; tone: Tone }> = {
  done: { says: 'worked', tone: 'ok' }, failed: { says: 'failed', tone: 'bad' }, stopped: { says: 'was stopped', tone: 'mute' },
  requeued: { says: 'was interrupted; the system put it back in the queue (automatic retry)', tone: 'warn' },
  paused: { says: 'paused to free its engine for a live stream; it goes on later', tone: 'mute' },
  held: { says: 'fetched the source; waiting for the customer to trim it', tone: 'mute' },
  running: { says: 'is running', tone: 'info' },
};
const byWho = (w?: Who) => (w ? (w.kind === 'staff' ? `staff (${w.label})` : `the customer, ${w.label}`) : 'someone');

function line(e: StoryEvent): { tone: Tone; text: ReactNode; sub?: ReactNode } {
  const wait = e.waitedS != null && e.waitedS >= 60 ? `, after waiting ${fmtDur(e.waitedS)} in the queue` : '';
  switch (e.kind) {
    case 'submitted': return { tone: 'info', text: <>Submitted by {e.who?.label ?? 'the customer'}</> };
    case 'try_started': return { tone: 'mute', text: <>Try {e.n} started{wait}</>, sub: e.backfilled ? 'Recorded from the job before tries were kept one by one (30 Oct): this is its latest try.' : undefined };
    case 'try_ended': {
      const t = TRY[e.outcome ?? ''] ?? { says: e.outcome ?? 'ended', tone: 'mute' as Tone };
      return {
        tone: t.tone,
        text: <>Try {e.n} {t.says}{e.tookS != null && e.outcome !== 'held' ? <span style={{ color: 'var(--ink-55)' }}> · took {fmtDur(e.tookS)}</span> : null}</>,
        sub: e.error && e.outcome !== 'done' ? <ErrorText shown={e.errorShown} raw={e.error} /> : undefined,
      };
    }
    case 'rec_started': return { tone: 'mute', text: <>Recording started</> };
    case 'stream_playing': return { tone: 'ok', text: <>The stream started sending audio</> };
    case 'stream_failed': return { tone: 'bad', text: <>The stream failed{e.bytesIn ? '' : ' before any audio arrived'}</>, sub: <><ErrorText shown={e.errorShown} raw={e.error} />{e.bytesIn ? <span style={{ display: 'block', color: 'var(--ink-55)' }}>{e.bytesIn.toLocaleString()} bytes had arrived</span> : null}</> };
    case 'rec_ended': return e.neverStarted
      ? { tone: 'bad', text: <>Recording ended: it never started</>, sub: <>No audio arrived and no transcript line was made <span className="mono" style={{ color: 'var(--ink-45)' }}>(recorded as {e.endReason ?? '—'})</span></> }
      : { tone: 'ok', text: <>Recording ended · {fmtDur(e.audioS ?? 0)} of audio, {(e.lines ?? 0).toLocaleString()} transcript line{e.lines === 1 ? '' : 's'}</>, sub: e.endReason ? <span className="mono" style={{ color: 'var(--ink-45)' }}>{e.endReason}</span> : undefined };
    case 'retry': return { tone: e.who?.kind === 'staff' ? 'info' : 'warn', text: <>Retried by {byWho(e.who)}{e.bulk ? ' (bulk retry)' : ''}</> };
    case 'stop': return { tone: 'mute', text: <>Stopped by {byWho(e.who)}</>, sub: e.reason ?? undefined };
    case 'fail': return { tone: 'bad', text: <>Failed on purpose by {byWho(e.who)}</>, sub: e.reason ?? undefined };
    case 'resolve': return { tone: 'mute', text: <>Set aside by {byWho(e.who)}: no longer counted as failed</>, sub: e.reason ?? undefined };
    case 'kill_claim': return { tone: 'warn', text: <>Claim released by {byWho(e.who)}</> };
    default: return { tone: 'mute', text: <>{e.kind} by {byWho(e.who)}</> };
  }
}

export function ErrorText({ shown, raw }: { shown?: string | null; raw?: string | null }) {
  if (!raw && !shown) return null;
  const same = !shown || !raw || shown.toLowerCase().replace(/\W/g, '').startsWith(raw.toLowerCase().replace(/\W/g, '').slice(0, 30));
  return (
    <span style={{ display: 'block' }}>
      <span style={{ color: 'var(--ink-80)', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{shown ?? raw}</span>
      {!same && <span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-45)', marginTop: 2, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>raw: {raw}</span>}
    </span>
  );
}

const DOT: Record<Tone, string> = { ok: 'var(--ok)', bad: 'var(--conf-low)', warn: 'var(--conf-mid)', info: 'var(--brand)', live: 'var(--live)', mute: 'var(--ink-40)' };

export function StoryBlock({ story, outcome, tries, retries, mobile }: { story: Story; outcome?: string | null; tries?: number | null; retries?: Retries | null; mobile: boolean }) {
  const o = OUTCOME[outcome as Outcome];
  const n = tries ?? story.attempts.length;
  const failedTries = story.attempts.filter((a) => a.outcome === 'failed').length;
  const sentence = [
    o ? o.says : outcome ?? '',
    n > 1 || failedTries ? `${n} ${n === 1 ? 'try' : 'tries'}${failedTries ? `, ${failedTries} failed` : ''}` : '',
    retriesText(retries) ?? (n > 1 && !retries?.customer && !retries?.staff ? 'no one retried it; the system did' : outcome === 'failed' ? 'never retried' : ''),
  ].filter(Boolean).join(' · ');
  const rows: Row[] = story.attempts.map((a) => ({
    key: String(a.n),
    cells: [
      { t: String(a.n), mono: true },
      { pill: { label: (a.outcome === 'done' ? 'worked' : a.outcome).toUpperCase(), tone: TRY[a.outcome]?.tone ?? 'mute' } },
      { t: a.startedAt ? fmtDate(a.startedAt) : 'never started', fg: a.startedAt ? undefined : 'var(--ink-45)', sub: a.waitedS != null && a.waitedS >= 60 ? `waited ${fmtDur(a.waitedS)}` : undefined },
      { t: a.tookS != null ? fmtDur(a.tookS) : '—', mono: true },
      { t: a.error ? <ErrorText shown={a.errorShown} raw={a.error} /> : '—', fg: a.error ? undefined : 'var(--ink-40)' },
    ],
  }));
  return (
    <>
      <DBlock title="What happened" sub={sentence}>
        <ol style={{ listStyle: 'none', margin: 0, padding: '6px 14px 12px' }}>
          {story.events.map((e, i) => {
            const l = line(e);
            return (
              <li key={i} style={{ display: 'grid', gridTemplateColumns: mobile ? '14px 1fr' : '118px 14px 1fr', columnGap: 10, padding: '6px 0', borderBottom: i < story.events.length - 1 ? '1px solid var(--line, var(--fill-08))' : undefined }}>
                {!mobile && <span className="mono" style={{ fontSize: 11, color: 'var(--ink-55)', paddingTop: 2 }}>{fmtDate(e.at)}</span>}
                <span style={{ width: 8, height: 8, borderRadius: 4, background: DOT[l.tone], marginTop: 6 }} />
                <span style={{ fontSize: 12.5, minWidth: 0 }}>
                  {mobile && <span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-55)' }}>{fmtDate(e.at)}</span>}
                  <span style={{ color: l.tone === 'bad' ? 'var(--conf-low)' : 'var(--ink-90, var(--ink-80))', fontWeight: e.kind === 'retry' || e.kind === 'submitted' ? 600 : 500 }}>{l.text}</span>
                  {l.sub && <span style={{ display: 'block', fontSize: 11.5, fontWeight: 300, marginTop: 2 }}>{l.sub}</span>}
                </span>
              </li>
            );
          })}
          <li style={{ display: 'grid', gridTemplateColumns: mobile ? '14px 1fr' : '118px 14px 1fr', columnGap: 10, padding: '8px 0 0' }}>
            {!mobile && <span className="mono" style={{ fontSize: 11, color: 'var(--ink-55)' }}>now</span>}
            <span />
            <span>{o ? <Pill tone={o.tone}>{o.label}</Pill> : null} <span style={{ fontSize: 12.5, marginLeft: 6 }}>{o?.says}</span></span>
          </li>
        </ol>
        {!story.complete && <div style={{ padding: '0 14px 12px', fontSize: 11.5, color: 'var(--ink-55)' }}>Tries from before 30 Oct were not kept one by one: the retries above come from the audit log, and only the latest try's times and error are known.</div>}
      </DBlock>
      {story.attempts.length > 0 && (
        <DBlock title="Tries" count={String(story.attempts.length)}>
          <div className="tbl--wrap">
            <Table mobile={mobile} rows={rows} cols={[{ label: '#', width: '28px' }, { label: 'RESULT', width: '84px' }, { label: 'STARTED', width: 'minmax(92px,.8fr)' }, { label: 'TOOK', width: '52px', align: 'r' }, { label: 'ERROR', width: 'minmax(160px,2.6fr)' }]} />
          </div>
        </DBlock>
      )}
    </>
  );
}

interface Line { seq: number; speaker: number; start: number; end: number; text: string }
interface Lines { lines: Line[]; total: number; speakers: number; next: number | null }
const at = (s: number) => { s = Math.max(0, Math.floor(s)); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60; return h ? `${h}:${String(m).padStart(2, '0')}:${String(x).padStart(2, '0')}` : `${m}:${String(x).padStart(2, '0')}`; };

export function TranscriptLines({ recording, title, count }: { recording: string; title: string; count: number }) {
  const { env, mobile } = useAdmin();
  const [d, setD] = useState<Lines | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = async (after: number) => {
    setBusy(true);
    try {
      const r = await get<Lines>(env, `/admin/recordings/${recording}/lines?after=${after}&limit=200`);
      setD((p) => (p && after >= 0 ? { ...r, lines: [...p.lines, ...r.lines] } : r));
      setErr(null);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { setD(null); if (count) void load(-1); }, [recording, count]); // eslint-disable-line react-hooks/exhaustive-deps
  const many = (d?.speakers ?? 0) > 1;
  return (
    <DBlock title={title} count={count.toLocaleString()} sub={count ? `${count.toLocaleString()} line${count === 1 ? '' : 's'}${many ? ` · ${d?.speakers} speakers` : ''}` : undefined}
      empty={count ? undefined : 'No transcript: no line was transcribed in this recording.'} loading={!!count && !d && !err} error={err ? { title: 'The transcript could not be read', text: err, retry: () => void load(-1) } : null}>
      {d && (
        <div style={{ maxHeight: 460, overflowY: 'auto', padding: '4px 14px 10px' }}>
          {d.lines.map((l) => (
            <div key={l.seq} style={{ display: 'grid', gridTemplateColumns: mobile ? '52px 1fr' : `64px ${many ? '84px ' : ''}1fr`, columnGap: 10, padding: '5px 0', borderBottom: '1px solid var(--fill-08)', fontSize: 12.5, lineHeight: 1.45 }}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--ink-55)', paddingTop: 1 }} title={`${at(l.start)} to ${at(l.end)}`}>{at(l.start)}</span>
              {many && !mobile && <span style={{ fontSize: 11.5, color: 'var(--ink-62)', fontWeight: 500 }}>Speaker {l.speaker + 1}</span>}
              <span style={{ color: 'var(--ink-90, var(--ink-80))', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{many && mobile ? <b style={{ fontWeight: 500, color: 'var(--ink-62)' }}>S{l.speaker + 1} </b> : null}{l.text}</span>
            </div>
          ))}
          {d.next != null && (
            <div style={{ padding: '10px 0 2px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void load(d.next!)}>{busy ? 'Loading…' : 'Show more'}</button>
              <span style={{ fontSize: 11.5, color: 'var(--ink-55)' }}>{d.lines.length.toLocaleString()} of {d.total.toLocaleString()} shown</span>
            </div>
          )}
        </div>
      )}
    </DBlock>
  );
}
